import { BodyCaption } from "./body-caption"

export type RiskRank = "low" | "moderate" | "high" | "critical"

export interface RiskProgressBarProps {
  title: string
  value: number // 0-100
  rank: RiskRank // Generic risk rank (low, moderate, high, critical)
  color: string // RGB color value for the progress bar
  className?: string // For width customization (e.g., "w-3/12", "w-full")
  maxWidth?: number // Maximum width in pixels for the progress bar container (defaults to flex-grow if not specified)
}

/**
 * RiskProgressBar - Pure View component for displaying a labeled progress bar for any risk metric
 *
 * Displays a title and a color-coded progress bar. The color and rank are passed in as props,
 * keeping this component completely pure and free of business logic.
 *
 * This is a pure View component - no MobX, no side effects, fully Storybook-ready.
 * Can be used for dilution risk, transaction failure risk, or any other risk metric.
 */
export const RiskProgressBar = ({ title, value, rank, color, className, maxWidth }: RiskProgressBarProps) => {
  // Clamp value to 0-100 range
  const clampedValue = Math.max(0, Math.min(100, value))

  return (
    <div className={`flex flex-col gap-1.5 ${className ?? ""}`}>
      <BodyCaption className="text-t300">{title}</BodyCaption>
      <div className="flex items-center gap-2">
        <div
          className="flex-grow overflow-hidden rounded-full bg-p500"
          style={maxWidth ? { maxWidth: `${maxWidth}px`, width: `${maxWidth}px` } : undefined}
        >
          <div
            className="h-2 transition-all duration-300"
            style={{
              width: `${clampedValue}%`,
              backgroundColor: color,
            }}
          />
        </div>
      </div>
    </div>
  )
}
